import {
  Component,
  OnChanges,
  SimpleChanges,
  input,
  output,
  signal,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
  ElementRef,
  computed,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { MenuGroup, MenuPage } from '../../../core/models/menu-item.model';
import { User } from '../../../core/models/user.model';
import { environment } from '../../../../environments/environment';

interface FlyoutState {
  group: MenuGroup;
  top: number;
  left: number;
  maxHeight: number;
}

/** Separación entre el sidebar comprimido y su panel flotante. */
const FLYOUT_GAP = 8;
/** Margen mínimo del panel respecto al borde de la ventana. */
const FLYOUT_VIEWPORT_MARGIN = 8;
/** Alto mínimo que se reserva al posicionar el panel cerca del borde inferior. */
const FLYOUT_MIN_HEIGHT = 240;
/** Espera antes de cerrar al salir con el mouse: permite cruzar del botón al panel. */
const FLYOUT_CLOSE_DELAY_MS = 180;

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule],
  template: `
    <aside class="sidebar" [class.sidebar--collapsed]="isCollapsed()">
      <!-- Logo Area -->
      <div class="sidebar__header">
        <!-- Atajo de ratón: por teclado están los botones de contraer/expandir. -->
        <div class="sidebar__logo" role="presentation"
             (click)="!isMobile() && toggleCollapse.emit()"
             [class.sidebar__logo--clickable]="!isMobile()">
          <div class="sidebar__logo-icon">R</div>
          @if (!isCollapsed()) {
            <span class="sidebar__logo-text">RBI Admin</span>
          }
        </div>
        @if (!isCollapsed()) {
          <button type="button" class="sidebar__toggle" (click)="toggleCollapse.emit()" aria-label="Contraer menú">
            <i class="pi pi-angle-left"></i>
          </button>
        }
      </div>

      <!-- Navigation -->
      <nav class="sidebar__nav">
        @for (group of menuGroups(); track group.id) {
          <div class="sidebar__section">
            @if (group.routerLink) {
              <!-- Enlace directo (Dashboard): sin secciones que desplegar -->
              @if (!isCollapsed()) {
                <a class="sidebar__module-header" [routerLink]="group.routerLink"
                  routerLinkActive="sidebar__link--active" (click)="handleGroupLinkClick()">
                  <span class="sidebar__module-header-content">
                    <span class="sidebar__module-icon-wrap">
                      <i [class]="group.icon || 'pi pi-fw pi-box'" class="sidebar__module-icon"></i>
                    </span>
                    <span class="sidebar__module-title">{{ group.label }}</span>
                  </span>
                </a>
              } @else {
                <a class="sidebar__module-btn" [routerLink]="group.routerLink" routerLinkActive="sidebar__module-btn--active"
                  [attr.aria-label]="group.label" [attr.title]="group.label" (click)="handleGroupLinkClick()">
                  <i [class]="group.icon || 'pi pi-fw pi-box'"></i>
                </a>
              }
            } @else if (!isCollapsed()) {
              <button type="button" class="sidebar__module-header"
                [attr.aria-expanded]="isGroupExpanded(group.id)" (click)="toggleGroup(group.id)">
                <span class="sidebar__module-header-content">
                  <span class="sidebar__module-icon-wrap">
                    <i [class]="group.icon || 'pi pi-fw pi-box'" class="sidebar__module-icon"></i>
                  </span>
                  <span class="sidebar__module-title">{{ group.label }}</span>
                </span>
                <i [class]="isGroupExpanded(group.id) ? 'pi pi-minus' : 'pi pi-plus'"
                   class="sidebar__module-toggle-icon"></i>
              </button>
            } @else {
              <!-- Comprimido: un ícono por módulo; su contenido se ve en el panel flotante -->
              <button type="button"
                class="sidebar__module-btn"
                [class.sidebar__module-btn--active]="activeGroupIds().has(group.id)"
                [class.sidebar__module-btn--open]="flyout()?.group?.id === group.id"
                [attr.aria-label]="group.label"
                [attr.title]="group.label"
                aria-haspopup="menu"
                [attr.aria-expanded]="flyout()?.group?.id === group.id"
                (mouseenter)="openFlyout(group, $event)"
                (mouseleave)="scheduleFlyoutClose()"
                (click)="openFlyout(group, $event)">
                <i [class]="group.icon || 'pi pi-fw pi-box'"></i>
              </button>
            }

            @if (!isCollapsed() && isGroupExpanded(group.id)) {
              @for (menuSection of group.sections; track menuSection.id) {
                <div class="sidebar__tree-section"
                     [class.sidebar__tree-section--collapsed]="!isSectionExpanded(group.id, menuSection.id)">
                  <button type="button"
                    class="sidebar__section-label"
                    [attr.aria-expanded]="isSectionExpanded(group.id, menuSection.id)"
                    (click)="toggleSection(group.id, menuSection.id, $event)">
                    <i class="pi pi-angle-down sidebar__section-icon"
                       [class.sidebar__section-icon--collapsed]="!isSectionExpanded(group.id, menuSection.id)"></i>
                    <span>{{ menuSection.label }}</span>
                  </button>
                  <ul class="sidebar__menu sidebar__section-pages">
                    @for (page of menuSection.pages; track page.id + '-' + page.label) {
                      @if (page.children && page.children.length) {
                        <!-- Ítem con sub-ítems colapsables -->
                        <li class="sidebar__page-item sidebar__page-item--parent"
                            [class.sidebar__page-item--open]="isPageExpanded(page.id)">
                          <button type="button"
                            class="sidebar__link sidebar__link--parent"
                            [class.sidebar__link--active]="isParentActive(page)"
                            [attr.aria-expanded]="isPageExpanded(page.id)"
                            (click)="togglePage(page.id, $event)">
                            <span class="sidebar__link-icon-wrap">
                              <i [class]="getPageIcon(page, group.icon)" class="sidebar__link-icon"></i>
                            </span>
                            <span class="sidebar__link-text" [attr.title]="page.label">{{ page.label }}</span>
                            <span class="sidebar__link-badge">{{ page.children.length }}</span>
                            <i class="pi pi-angle-down sidebar__link-caret"
                               [class.sidebar__link-caret--open]="isPageExpanded(page.id)"></i>
                          </button>
                          <ul class="sidebar__submenu">
                            @for (child of page.children; track child.id + '-' + child.label) {
                              <li>
                                <a [routerLink]="child.routerLink"
                                  [queryParams]="child.queryParams || null"
                                  class="sidebar__sublink"
                                  [class.sidebar__sublink--active]="isChildActive(child)"
                                  (click)="handleItemClick(child, $event)"
                                  [class.sidebar__link--disabled]="child.disabled">
                                  <span class="sidebar__link-text" [attr.title]="child.label">{{ child.label }}</span>
                                </a>
                              </li>
                            }
                          </ul>
                        </li>
                      } @else {
                        <li class="sidebar__page-item">
                          <a [routerLink]="page.routerLink"
                            [queryParams]="page.queryParams || null"
                            class="sidebar__link"
                            routerLinkActive="sidebar__link--active"
                            [routerLinkActiveOptions]="{ exact: false }"
                            (click)="handleItemClick(page, $event)"
                            [class.sidebar__link--disabled]="page.disabled">
                            <span class="sidebar__link-icon-wrap">
                              <i [class]="getPageIcon(page, group.icon)" class="sidebar__link-icon"></i>
                            </span>
                            <span class="sidebar__link-text" [attr.title]="page.label">{{ page.label }}</span>
                          </a>
                        </li>
                      }
                    }
                  </ul>
                </div>
              }
            }
          </div>
        }
      </nav>

      <!-- Panel del módulo con el sidebar comprimido: secciones, grupos y páginas con nombre -->
      @if (flyout(); as open) {
        <div #flyoutPanel
          class="sidebar__flyout"
          role="menu"
          tabindex="-1"
          [attr.aria-label]="open.group.label"
          [style.top.px]="open.top"
          [style.left.px]="open.left"
          [style.max-height.px]="open.maxHeight"
          (mouseenter)="cancelFlyoutClose()"
          (mouseleave)="scheduleFlyoutClose()"
          (keydown.escape)="closeFlyout(true)">
          <div class="sidebar__flyout-title">
            <i [class]="open.group.icon || 'pi pi-fw pi-box'"></i>
            <span>{{ open.group.label }}</span>
          </div>
          @for (menuSection of open.group.sections; track menuSection.id) {
            <div class="sidebar__flyout-section">{{ menuSection.label }}</div>
            <ul class="sidebar__flyout-list">
              @for (page of menuSection.pages; track page.id + '-' + page.label) {
                @if (page.children && page.children.length) {
                  <li class="sidebar__flyout-group">
                    <span class="sidebar__flyout-group-label">
                      <i [class]="getPageIcon(page, open.group.icon)"></i>
                      <span [attr.title]="page.label">{{ page.label }}</span>
                    </span>
                    <ul class="sidebar__flyout-list sidebar__flyout-list--nested">
                      @for (child of page.children; track child.id + '-' + child.label) {
                        <li>
                          <a [routerLink]="child.routerLink"
                            [queryParams]="child.queryParams || null"
                            role="menuitem"
                            class="sidebar__flyout-link sidebar__flyout-link--child"
                            [class.sidebar__flyout-link--active]="isChildActive(child)"
                            [class.sidebar__link--disabled]="child.disabled"
                            (click)="onFlyoutLinkClick(child, $event)">
                            <span [attr.title]="child.label">{{ child.label }}</span>
                          </a>
                        </li>
                      }
                    </ul>
                  </li>
                } @else {
                  <li>
                    <a [routerLink]="page.routerLink"
                      [queryParams]="page.queryParams || null"
                      role="menuitem"
                      class="sidebar__flyout-link"
                      [class.sidebar__flyout-link--active]="isChildActive(page)"
                      [class.sidebar__link--disabled]="page.disabled"
                      (click)="onFlyoutLinkClick(page, $event)">
                      <i [class]="getPageIcon(page, open.group.icon)"></i>
                      <span [attr.title]="page.label">{{ page.label }}</span>
                    </a>
                  </li>
                }
              }
            </ul>
          }
        </div>
      }

      @if (isCollapsed() && !isMobile()) {
        <button type="button" class="sidebar__expand" (click)="toggleCollapse.emit()" aria-label="Expandir menú">
          <i class="pi pi-angle-right"></i>
        </button>
      }

      <!-- Version -->
      @if (!isCollapsed()) {
        <div class="sidebar__version">v{{ appVersion }}</div>
      }
    </aside>
  `,
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class SidebarComponent implements OnChanges {
  readonly appVersion = environment.appVersion;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly collapsed = input<boolean>(false);
  readonly menuGroups = input<MenuGroup[]>([]);
  readonly currentUser = input<User | null>(null);
  readonly toggleCollapse = output<void>();
  readonly itemSelected = output<void>();

  // Grupo expandido (acordeón: uno a la vez).
  activeGroupId: number | null = null;
  // Ítems-padre con sub-ítems expandidos (señal → reactividad con OnPush).
  readonly expandedPages = signal<Set<number>>(new Set<number>());
  // Secciones abiertas; por defecto todas plegadas salvo la de la ruta actual. La clave
  // incluye el grupo porque los ids de sección solo son únicos dentro de su módulo.
  readonly expandedSections = signal<Set<string>>(new Set<string>());
  // Padres que el usuario tocó manualmente → el auto-expandir ya no los gestiona.
  private readonly userToggled = new Set<number>();
  // URL actual (señal) para resaltar padre/hijo activo, incl. queryParams.
  readonly currentUrl = signal<string>('');

  // ── Panel flotante del modo comprimido ─────────────────────────────────────────
  readonly flyout = signal<FlyoutState | null>(null);
  private readonly flyoutPanel = viewChild<ElementRef<HTMLElement>>('flyoutPanel');
  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Módulos que contienen la ruta actual: su botón se resalta con el sidebar comprimido. */
  readonly activeGroupIds = computed(() => {
    this.currentUrl();
    const ids = new Set<number>();
    for (const group of this.menuGroups()) {
      const hasActivePage = group.sections.some((section) =>
        section.pages.some((page) => this.isChildActive(page) || this.isParentActive(page)),
      );
      if (hasActivePage) {
        ids.add(group.id);
      }
    }
    return ids;
  });

  constructor() {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.currentUrl.set(e.urlAfterRedirects);
        this.expandActiveParents();
        this.expandActiveSections();
        this.closeFlyout();
      });
    this.destroyRef.onDestroy(() => this.cancelFlyoutClose());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['collapsed']) {
      this.closeFlyout();
    }
    if (changes['menuGroups']) {
      const groups = this.menuGroups();
      if (this.activeGroupId === null && groups.length > 0) {
        this.activeGroupId = groups[0].id;
      }
      this.expandActiveParents();
      this.expandActiveSections();
    }
  }

  // ── Grupos (módulos) ─────────────────────────────────────────────────────────
  isGroupExpanded(groupId: number): boolean {
    return this.activeGroupId === groupId;
  }

  /** Un enlace directo cierra el menú móvil al elegirlo, igual que una página. */
  handleGroupLinkClick(): void {
    if (this.isMobile()) {
      this.itemSelected.emit();
    }
  }

  toggleGroup(groupId: number): void {
    this.activeGroupId = this.activeGroupId === groupId ? null : groupId;
  }

  // ── Secciones colapsables ────────────────────────────────────────────────────
  isSectionExpanded(groupId: number, sectionId: number): boolean {
    return this.expandedSections().has(this.sectionKey(groupId, sectionId));
  }

  toggleSection(groupId: number, sectionId: number, event?: Event): void {
    event?.stopPropagation();
    const key = this.sectionKey(groupId, sectionId);
    const next = new Set(this.expandedSections());
    if (!next.delete(key)) {
      next.add(key);
    }
    this.expandedSections.set(next);
  }

  private sectionKey(groupId: number, sectionId: number): string {
    return `${groupId}:${sectionId}`;
  }

  /** Abre la sección que contiene la ruta actual: arrancan plegadas, pero la página
   *  en pantalla nunca debe quedar escondida bajo una sección cerrada. */
  private expandActiveSections(): void {
    const next = new Set(this.expandedSections());
    let changed = false;
    for (const group of this.menuGroups()) {
      for (const section of group.sections) {
        const key = this.sectionKey(group.id, section.id);
        if (next.has(key)) {
          continue;
        }

        const hasActivePage = section.pages.some(
          (page) => this.isChildActive(page) || this.isParentActive(page),
        );
        if (hasActivePage) {
          next.add(key);
          changed = true;
        }
      }
    }
    if (changed) {
      this.expandedSections.set(next);
    }
  }

  // ── Ítems-padre colapsables ───────────────────────────────────────────────────
  isPageExpanded(pageId: number): boolean {
    return this.expandedPages().has(pageId);
  }

  togglePage(pageId: number, event?: Event): void {
    event?.stopPropagation();
    this.userToggled.add(pageId); // a partir de aquí manda el usuario
    const next = new Set(this.expandedPages());
    if (!next.delete(pageId)) {
      next.add(pageId);
    }
    this.expandedPages.set(next);
  }

  /** Abre el padre cuyo hijo coincide con la ruta actual, salvo que el usuario
   *  ya lo haya gestionado manualmente (no peleamos con su decisión). */
  private expandActiveParents(): void {
    const next = new Set(this.expandedPages());
    let changed = false;
    for (const group of this.menuGroups()) {
      for (const section of group.sections) {
        for (const page of section.pages) {
          if (
            page.children?.length &&
            this.isParentActive(page) &&
            !next.has(page.id) &&
            !this.userToggled.has(page.id)
          ) {
            next.add(page.id);
            changed = true;
          }
        }
      }
    }
    if (changed) {
      this.expandedPages.set(next);
    }
  }

  isParentActive(page: MenuPage): boolean {
    return (page.children ?? []).some((child) => this.isChildActive(child));
  }

  isChildActive(child: MenuPage): boolean {
    if (!child.routerLink) {
      return false;
    }
    const [urlPath, urlQuery = ''] = this.currentUrl().split('?');
    const childPath = child.routerLink.split('?')[0];
    const pathMatches = urlPath === childPath || urlPath.startsWith(`${childPath}/`);
    if (!pathMatches) {
      return false;
    }
    if (!child.queryParams) {
      return true;
    }
    const params = new URLSearchParams(urlQuery);
    return Object.entries(child.queryParams).every(([key, value]) => params.get(key) === String(value));
  }

  // ── Panel flotante (sidebar comprimido) ──────────────────────────────────────────
  openFlyout(group: MenuGroup, event: Event): void {
    this.cancelFlyoutClose();
    const button = event.currentTarget as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    const sidebarRight = button.closest('.sidebar')?.getBoundingClientRect().right ?? buttonRect.right;
    const top = Math.max(
      FLYOUT_VIEWPORT_MARGIN,
      Math.min(buttonRect.top, window.innerHeight - FLYOUT_MIN_HEIGHT - FLYOUT_VIEWPORT_MARGIN),
    );
    this.flyout.set({
      group,
      top,
      left: sidebarRight + FLYOUT_GAP,
      maxHeight: window.innerHeight - top - FLYOUT_VIEWPORT_MARGIN,
    });

    // Abierto con teclado (Enter/Espacio): el foco entra al panel para poder recorrerlo.
    if (event instanceof MouseEvent && event.type === 'click' && event.detail === 0) {
      setTimeout(() => this.flyoutPanel()?.nativeElement.querySelector<HTMLElement>('a')?.focus());
    }
  }

  scheduleFlyoutClose(): void {
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => this.flyout.set(null), FLYOUT_CLOSE_DELAY_MS);
  }

  cancelFlyoutClose(): void {
    if (this.flyoutCloseTimer) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  closeFlyout(returnFocus = false): void {
    const open = this.flyout();
    this.cancelFlyoutClose();
    this.flyout.set(null);
    if (returnFocus && open) {
      document.querySelector<HTMLElement>(`.sidebar__module-btn[aria-label="${CSS.escape(open.group.label)}"]`)?.focus();
    }
  }

  onFlyoutLinkClick(page: MenuPage, event: Event): void {
    this.handleItemClick(page, event);
    this.closeFlyout();
  }

  /** Un clic fuera del panel y de los botones de módulo lo cierra (abierto con clic o teclado). */
  onDocumentClick(event: MouseEvent): void {
    if (!this.flyout()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.sidebar__flyout, .sidebar__module-btn')) return;
    this.closeFlyout();
  }

  // ── Estado general ─────────────────────────────────────────────────────────────
  isCollapsed(): boolean {
    if (this.isMobile()) return false;
    return this.collapsed();
  }

  isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  handleItemClick(page: MenuPage, event: Event): void {
    if (page.disabled || !page.routerLink) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isMobile()) {
      this.itemSelected.emit();
    }
  }

  getPageIcon(page: MenuPage, groupIcon?: string): string {
    if (page.icon) {
      return page.icon;
    }
    const key = `${page.label} ${page.routerLink ?? ''}`.toLowerCase();

    if (key.includes('notificaciones')) return 'pi pi-bell';
    if (key.includes('visitas')) return 'pi pi-calendar';
    if (key.includes('promociones')) return 'pi pi-megaphone';
    if (key.includes('kpi')) return 'pi pi-chart-pie';
    if (key.includes('mapa pdv') || key.includes('/mapa')) return 'pi pi-map-marker';
    if (key.includes('reporte')) return 'pi pi-chart-line';
    if (key.includes('power sku')) return 'pi pi-bolt';
    if (key.includes('sincronizador') || key.includes('/sincronizador')) return 'pi pi-fw pi-sync';
    if (key.includes('catalogo-reprico') || key.includes('catálogo reprico')) return 'pi pi-box';
    if (key.includes('registro')) return 'pi pi-building-columns';
    if (key.includes('flujo')) return 'pi pi-arrows-h';
    if (key.includes('mensuales')) return 'pi pi-chart-line';
    if (key.includes('balance')) return 'pi pi-wallet';
    if (key.includes('usuarios')) return 'pi pi-users';
    if (key.includes('roles')) return 'pi pi-shield';
    if (key.includes('datos maestros')) return 'pi pi-database';
    if (key.includes('paginas') || key.includes('páginas')) return 'pi pi-file';
    if (key.includes('dashboard')) return 'pi pi-chart-pie';

    return groupIcon || 'pi pi-circle-fill';
  }
}
